import { PlatformApiMethods } from '../../../../types/platformApi';
import type { IntentInstallStore } from '../IntentInstallStore';
import { TIMEOUT_BETWEEN_FAKE_STEPS } from '../constants';
import { getTranslation } from '../translation';
import { ProgressStore } from './ProgressStore';
import { PROGRESS_BAR_IS_NOT_OPEN_ERROR } from './constants';

type UpdateProgressBarOptions = {
  /**
   * If should reopen progress bar if receive an error that there is no progress bar open
   */
  keepAlive?: boolean;
};

export class ProgressBarManager {
  private progressStore: ProgressStore;
  private fakeProgressIntervalId: NodeJS.Timeout | null;

  // when there is no appDefinitionId in the appData, a progress bar is considered to be open by the editor,
  // as an app doesn't control appData and it's getting bound by the editor SDK automatically
  // we need it to ensure that no app will be able to affect our progress bar
  private progressBarAppData = {};

  private lastTitle: string;

  constructor(
    private platformApiMethods: PlatformApiMethods,
    private intentStore: IntentInstallStore,
  ) {
    const appsToInstall = intentStore.getAppsToInstall();
    this.progressStore = new ProgressStore({
      appDefinitionIds: appsToInstall,
    });
    this.fakeProgressIntervalId = null;
    this.lastTitle = getTranslation(this.intentStore, 'header');
  }

  private updateProgressBar(title: string, options?: UpdateProgressBarOptions) {
    this.lastTitle = title;

    const isFinished =
      this.progressStore.getCurrentStep() ===
      this.progressStore.getTotalSteps();
    const text = isFinished
      ? getTranslation(this.intentStore, 'lastStep')
      : getTranslation(this.intentStore, 'stepPrefix', {
          transformKey: (key) => key + this.progressStore.getCurrentAppStep(),
        });

    this.platformApiMethods.editor
      .updateProgressBar(this.progressBarAppData, '', {
        currentStep: this.progressStore.getCurrentStep(),
        stepTitle: text,
        title,
      })
      .catch((err: Error) => {
        const msg = err?.message || '';
        // in some cases progress bar can be closed outside
        // and we want to re-open it only when it's save
        // as opening a progress bar closes all open panels
        if (
          options?.keepAlive &&
          msg.includes(PROGRESS_BAR_IS_NOT_OPEN_ERROR)
        ) {
          this.clearProgressInterval();

          this.openInitialProgressBar(this.lastTitle);

          this.startFakeProgressBarForCurrentApp(true);
        }
      });
  }

  private startFakeProgress(title: string) {
    this.clearProgressInterval();

    this.updateProgressBar(title, { keepAlive: true });

    if (
      this.progressStore.getInstalledCounter() ===
      this.progressStore.getTotalApps()
    ) {
      return;
    }

    this.fakeProgressIntervalId = setInterval(() => {
      if (
        this.progressStore.getCurrentAppStep() ===
        this.progressStore.getStepsPerApp()
      ) {
        this.clearProgressInterval();
        return;
      }
      this.progressStore.incrementCurrentAppStep();
      this.updateProgressBar(title);
    }, TIMEOUT_BETWEEN_FAKE_STEPS);
  }

  public startFakeProgressBarForCurrentApp(isRestored = false) {
    if (!isRestored) {
      this.progressStore.setCurrentAppStep(1);
    }
    const nextApp =
      this.intentStore.getAppsToInstall()[
        this.progressStore.getInstalledCounter()
      ];

    // on the last step there is no nextApp, so show a title of the last installed before progress bar is closed
    const prevApp =
      this.intentStore.getAppsToInstall()[
        this.progressStore.getInstalledCounter() - 1
      ];

    const appName = this.intentStore.getAppName(nextApp || prevApp);
    this.startFakeProgress(
      getTranslation(this.intentStore, 'headerAfterFetch', {
        params: {
          app_name: appName,
        },
      }),
    );
  }

  public pauseProgressBar() {
    this.clearProgressInterval();

    this.closeProgressBar();

    return () => {
      this.openInitialProgressBar(this.lastTitle);

      this.startFakeProgressBarForCurrentApp(true);
    };
  }

  public openInitialProgressBar(title?: string) {
    this.platformApiMethods.editor.openProgressBar(
      this.progressBarAppData,
      '',
      {
        title: title || getTranslation(this.intentStore, 'header'),
        totalSteps: this.progressStore.getTotalSteps(),
        currentStep: this.progressStore.getCurrentStep(),
      },
    );
  }

  public startFakeProgressBarForNextApp() {
    this.clearProgressInterval();

    if (
      this.progressStore.getInstalledCounter() ===
      this.progressStore.getTotalApps()
    ) {
      this.progressStore.setCurrentAppStep(1);
      this.updateProgressBar(this.lastTitle);
      return;
    }

    this.progressStore.incrementInstalledCounter();
    this.startFakeProgressBarForCurrentApp();
  }

  public closeProgressBar(isError: boolean = false) {
    this.clearProgressInterval();

    this.platformApiMethods.editor.closeProgressBar(
      this.progressBarAppData,
      '',
      { isError },
    );
  }

  private clearProgressInterval() {
    if (!this.fakeProgressIntervalId) {
      return;
    }
    clearInterval(this.fakeProgressIntervalId);
    this.fakeProgressIntervalId = null;
  }
}
